import React, { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../App';

// --- Componentes de UI de Soporte (Completos) ---

const formatCurrency = (value) => `Q${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function KpiTotalCard({ title, data, colorClass, icon }) {
    const safeData = data || { totalProducto: 0, totalEnvio: 0, totalGeneral: 0 };
    return (
        <div className={`bg-white rounded-lg shadow-md p-5 border-l-4 ${colorClass}`}>
            <div className="flex items-center mb-3">{icon} <h4 className="font-bold text-gray-800 ml-2">{title}</h4></div>
            <div className="space-y-2 text-sm text-gray-700">
                <div className="flex justify-between"><span>Total de Producto:</span><span className="font-semibold">{formatCurrency(safeData.totalProducto)}</span></div>
                <div className="flex justify-between"><span>Total de Envío:</span><span className="font-semibold">{formatCurrency(safeData.totalEnvio)}</span></div>
                <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t"><span>Total General:</span><span>{formatCurrency(safeData.totalGeneral)}</span></div>
            </div>
        </div>
    );
}

function StatusPieChart({ data, total }) {
    const conicGradientStyle = useMemo(() => {
        if (total === 0) return { background: 'conic-gradient(#e5e7eb 0% 100%)' };
        let cumulativePercent = 0;
        const gradientParts = (data || []).map(item => {
            if (item.value === 0) return '';
            const percent = (item.value / total) * 100;
            const startPercent = cumulativePercent;
            cumulativePercent += percent;
            return `${item.color} ${startPercent}% ${cumulativePercent}%`;
        }).filter(Boolean).join(', ');
        return { background: `conic-gradient(${gradientParts})` };
    }, [data, total]);

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-700 mb-6 text-center">Distribución Total de Pedidos</h3>
            <div className="flex justify-center items-center my-4">
                <div className="relative w-48 h-48">
                    <div className="absolute w-full h-full rounded-full" style={conicGradientStyle}></div>
                    <div className="absolute w-32 h-32 bg-white rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col justify-center items-center">
                       <span className="text-3xl font-bold text-gray-800">{total}</span><span className="text-xs text-gray-500">Pedidos</span>
                    </div>
                </div>
            </div>
            <div className="flex justify-center flex-wrap gap-x-6 gap-y-3 mt-6">
                {(data || []).map(item => (
                     <div key={item.label} className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
                        <span className="text-sm text-gray-600">{`${item.label}: ${item.value} (${total > 0 ? ((item.value / total) * 100).toFixed(1) : 0}%)`}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function HighlightStatCard({ icon, title, value, unit, color }) {
    const colorClasses = { amber: 'bg-amber-100 text-amber-600', sky: 'bg-sky-100 text-sky-600', emerald: 'bg-emerald-100 text-emerald-600' };
    const selectedColor = colorClasses[color] || colorClasses.sky;
    return (
        <div className="bg-white p-5 rounded-lg shadow-md flex items-center space-x-4">
            <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full ${selectedColor}`}>{icon}</div>
            <div>
                <h4 className="text-gray-500 text-sm font-medium">{title}</h4>
                <p className="text-2xl font-bold text-gray-800">{value}{unit && <span className="text-lg font-medium">{unit}</span>}</p>
            </div>
        </div>
    );
}

const statusConfig = [
    { key: 'entregado', color: 'bg-lime-500', label: 'Entregados' },
    { key: 'enviado', color: 'bg-blue-500', label: 'Enviados' },
    { key: 'anulado', color: 'bg-amber-500', label: 'Anulados' },
    { key: 'devolucion', color: 'bg-red-500', label: 'Devoluciones' },
];

function CourierPerformanceChart({ data }) {
    const maxValue = useMemo(() => {
        if (!data || data.length === 0) return 1;
        const allValues = data.flatMap(d => statusConfig.map(s => d[s.key]));
        return Math.max(...allValues, 1);
    }, [data]);
    
    if (!data || data.length === 0) {
       return <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">No hay datos de rendimiento de mensajeros para este período.</div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-700 mb-6 text-center">Rendimiento por Mensajero (Gráfico)</h3>
            <div className="space-y-8">
                {data.map(({ mensajero, ...stats }) => (
                    <div key={mensajero} className="grid grid-cols-[auto_1fr] items-center gap-x-4">
                        <span className="text-right font-semibold text-gray-600 truncate">{mensajero}</span>
                        <div className="w-full bg-gray-200 rounded-full h-6 flex overflow-hidden">
                           {statusConfig.map(s => {
                                const value = stats[s.key] || 0;
                                if (value === 0) return null;
                                const widthPercentage = (value / maxValue) * 100;
                                return (
                                    <div key={s.key} 
                                         className={`flex items-center justify-center h-6 text-xs font-bold text-white ${s.color}`} 
                                         style={{ width: `${widthPercentage}%` }}
                                         title={`${s.label}: ${value}`}>
                                        {value}
                                    </div>
                                );
                           })}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-center items-center flex-wrap gap-x-4 gap-y-2 mt-6 pt-4 border-t">
                {statusConfig.map(s => (
                    <div key={s.key} className="flex items-center">
                        <div className={`w-4 h-4 mr-2 rounded ${s.color}`}></div>
                        <span className="text-sm">{s.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CourierPerformanceTable({ data }) {
    const tableRows = [
        { label: 'Total Pedidos', key: 'totalPedidos' },
        { label: 'Entregados', key: 'entregado' },
        { label: 'Enviados', key: 'enviado' },
        { label: 'Anulados', key: 'anulado' },
        { label: 'Devoluciones', key: 'devolucion' },
        { label: '% Efectividad', key: 'efectividad', format: 'percent' },
        { label: 'Total Recaudado', key: 'totalRecaudado', format: 'currency' },
    ];

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Rendimiento por Mensajero (Tabla)</h3>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse table-auto">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600 border border-gray-300 sticky left-0 bg-white z-10 whitespace-nowrap">Métrica</th>
                            {data.map(courier => (
                                <th key={courier.mensajero} className="p-3 text-center text-sm font-semibold text-gray-600 border border-gray-300 min-w-[120px]">{courier.mensajero}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map(row => (
                            <tr key={row.key} className="border-b hover:bg-gray-50">
                                <td className="p-3 font-semibold text-gray-700 border border-gray-300 sticky left-0 bg-white z-10 whitespace-nowrap">{row.label}</td>
                                {data.map(courier => (
                                    <td key={`${courier.mensajero}-${row.key}`} className={`p-3 text-center border border-gray-300 ${row.format === 'currency' ? 'font-bold' : ''}`}>
                                        {row.format === 'percent'
                                            ? `${courier[row.key] || 0}%`
                                            : row.format === 'currency'
                                            ? formatCurrency(courier[row.key])
                                            : (courier[row.key] || 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function DailyAverageStatsCard({ data }) {
    if (!data || !data.couriers || data.couriers.length === 0) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Promedio de Pedidos Diarios (Días Hábiles)</h3>
                <p className="text-sm text-gray-500">No hay datos de pedidos efectivos para calcular promedios en este período.</p>
            </div>
        );
    }
    
    const { couriers, totalAverage, businessDaysInPeriod } = data;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Promedio de Pedidos Diarios (Días Hábiles)</h3>
            <p className="text-sm text-gray-500 mb-6">Cálculo basado en pedidos efectivos y <span className="font-bold">{businessDaysInPeriod}</span> días hábiles en el período.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
                {couriers.map(courier => (
                    <div key={courier.mensajero} className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                        <p className="text-gray-800 font-semibold truncate" title={courier.mensajero}>{courier.mensajero}</p>
                        <p className="text-4xl font-bold text-sky-600">{courier.promedioDiario}</p>
                        <p className="text-xs text-gray-500">pedidos/día</p>
                    </div>
                ))}
            </div>

            <div className="border-t-2 border-gray-200 pt-6 flex justify-center items-center bg-slate-50 rounded-lg p-6">
                <div className="text-center">
                    <p className="text-gray-800 font-bold text-lg tracking-wide">Promedio General Diario</p>
                    <p className="text-6xl font-extrabold text-slate-700 my-2">{totalAverage}</p>
                    <p className="text-sm text-gray-600">pedidos efectivos por día hábil</p>
                </div>
            </div>
        </div>
    );
}

// --- NUEVO COMPONENTE PARA ESTADÍSTICAS DEL MES ---
function MonthlyInsightsCard({ data }) {
    if (!data || !data.busiestDays) {
        return null; // No renderizar si no hay datos
    }

    const Insight = ({ icon, color, title, value, subValue = '' }) => (
        <div className={`flex items-start p-4 bg-white rounded-lg shadow border-l-4 ${color}`}>
            <div className="mr-4 text-2xl">{icon}</div>
            <div>
                <p className="font-semibold text-gray-800">{title}</p>
                <p className="text-xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{subValue}</p>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">Estadísticas Clave del Mes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Promedios */}
                <Insight icon="💰" color="border-green-500" title="Promedio por Pedido" value={formatCurrency(data.avgOrderValue)} subValue="Total" />
                <Insight icon="📦" color="border-blue-500" title="Promedio por Producto" value={formatCurrency(data.avgProductValue)} />
                <Insight icon="🚚" color="border-orange-500" title="Promedio por Envío" value={formatCurrency(data.avgShippingValue)} />
                
                {/* Días con más/menos pedidos */}
                <Insight icon="📈" color="border-teal-500" title="Día(s) con Más Pedidos" value={`${data.busiestDays.count} pedidos`} subValue={data.busiestDays.days.join(', ')} />
                <Insight icon="📉" color="border-red-500" title="Día(s) con Menos Pedidos" value={`${data.quietestDays.count} pedidos`} subValue={data.quietestDays.days.join(', ')} />
            </div>
        </div>
    );
}

// --- MÓDULO PRINCIPAL DE KPIs ---
function KPIsModule({ permissions, showModal, currentUser }) {
    const [summaryData, setSummaryData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRecalculating, setIsRecalculating] = useState(false);
    
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= 2023; i--) {
            years.push(i);
        }
        return years;
    }, []);

    const monthOptions = useMemo(() => [
        { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
        { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
        { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
        { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
    ], []);

    useEffect(() => {
        setIsLoading(true);
        const docId = `summary_${selectedYear}_${String(selectedMonth).padStart(2, '0')}`;
        const summaryDocRef = doc(db, 'reports', docId);

        const unsubscribe = onSnapshot(summaryDocRef, (doc) => {
            if (doc.exists()) {
                setSummaryData(doc.data());
            } else {
                setSummaryData(null);
            }
            setIsLoading(false);
        }, (error) => {
            console.error("Error al obtener el resumen de KPIs:", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [selectedYear, selectedMonth]);

    const handleForceRecalculate = async () => {
        setIsRecalculating(true);
        const monthName = monthOptions.find(m => m.value === selectedMonth)?.label;
        showModal("Actualizando Datos", `Se ha iniciado el proceso de recálculo para ${monthName} de ${selectedYear}.`);
        
        const functions = getFunctions();
        const forceRecalculation = httpsCallable(functions, 'forceRecalculation');

        try {
            await forceRecalculation({ year: selectedYear, month: selectedMonth });
        } catch (error) {
            console.error("Error al forzar el recálculo:", error);
            showModal("Error", `No se pudo iniciar la actualización: ${error.message}`);
        } finally {
            setIsRecalculating(false);
        }
    };

    const kpiData = summaryData?.kpi_data;
    
    if (!permissions?.KPIS?.view) {
        return <div className="text-center py-10 bg-red-50 border border-red-200 rounded-lg shadow"><h2 className="text-2xl font-semibold text-red-700">Acceso Denegado</h2></div>;
    }

    const renderContent = () => {
        if (isLoading) {
            return <div className="text-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-sky-600 mx-auto mb-4"></div><p>Cargando KPIs...</p></div>;
        }
        if (!kpiData) {
            return (
                <div className="text-center py-10 bg-gray-50 rounded-lg shadow-inner">
                    <p className="text-gray-600">No hay datos de resumen disponibles para el período seleccionado.</p>
                    <p className="text-sm text-gray-500 mt-2">Prueba a seleccionar otro período o haz clic en "Calcular Ahora" para generar el reporte.</p>
                    {currentUser?.role === 'administrador' && (
                        <button onClick={handleForceRecalculate} disabled={isRecalculating} className="btn-primary mt-4 text-sm">
                            {isRecalculating ? 'Calculando...' : 'Calcular Ahora'}
                        </button>
                    )}
                </div>
            );
        }

        const distribution = [
            { label: 'Entregados', value: kpiData.totalsByStatus?.entregado?.count || 0, color: '#84cc16' },
            { label: 'Enviados', value: kpiData.totalsByStatus?.enviado?.count || 0, color: '#3b82f6' },
            { label: 'Anulados', value: kpiData.totalsByStatus?.anulado?.count || 0, color: '#f59e0b' },
            { label: 'Devoluciones', value: kpiData.totalsByStatus?.devolucion?.count || 0, color: '#ef4444' }
        ];
        const totalPackages = distribution.reduce((acc, item) => acc + item.value, 0);

        return (
            <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <KpiTotalCard title="Total Recaudado (Entregado)" data={kpiData.totalsByStatus?.entregado} colorClass="border-lime-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-lime-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>} />
                    <KpiTotalCard title="Total Pendiente (Enviado)" data={kpiData.totalsByStatus?.enviado} colorClass="border-blue-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
                    <KpiTotalCard title="Total Anulado" data={kpiData.totalsByStatus?.anulado} colorClass="border-amber-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
                    <KpiTotalCard title="Total Devuelto" data={kpiData.totalsByStatus?.devolucion} colorClass="border-red-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l-3 3m3-3l3 3m6 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <HighlightStatCard 
                        color="amber" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z" /></svg>}
                        title="Mensajero más Efectivo" value={`${kpiData.highlightStats?.mostEffectiveCourier?.name || 'N/A'} (${kpiData.highlightStats?.mostEffectiveCourier?.value || 0}%)`}
                    />
                    <HighlightStatCard
                        color="sky" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                        title="Cumplimiento Operativo" value={kpiData.highlightStats?.operationalCompliance || 0} unit="%"
                    />
                    <HighlightStatCard
                        color="emerald" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>}
                        title="Total de Pedidos (Mes)" value={kpiData.highlightStats?.totalOrders || 0}
                    />
                </div>
                <DailyAverageStatsCard data={kpiData.dailyAverageStats} />
                <CourierPerformanceTable data={kpiData.courierBreakdown} />
                <CourierPerformanceChart data={kpiData.courierBreakdown} />
                <StatusPieChart data={distribution} total={totalPackages} />
                <MonthlyInsightsCard data={kpiData.monthlyInsights} />
            </div>
        );
    };

    return (
        <div className="bg-gray-50 p-4 md:p-6 rounded-lg space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white p-4 rounded-lg shadow">
                <div className="flex-grow">
                    <h2 className="text-2xl font-semibold text-gray-800">KPIs / Resumen de Rendimiento</h2>
                    <p className="text-sm text-gray-500">
                        Mostrando datos para: <span className="font-semibold">{monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}</span>
                    </p>
                </div>
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                    {summaryData?.lastUpdated && (
                         <span className="text-xs text-gray-500 text-right">
                            Última Actualización: {new Date(summaryData.lastUpdated.toDate()).toLocaleString('es-GT')}
                        </span>
                    )}
                    <div className="flex items-center gap-2">
                        <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))} className="bg-white border border-gray-300 rounded-md shadow-sm py-1 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500">
                            {monthOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="bg-white border border-gray-300 rounded-md shadow-sm py-1 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500">
                            {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                        </select>
                        {currentUser?.role === 'administrador' && (
                            <button onClick={handleForceRecalculate} disabled={isRecalculating} className="btn-secondary py-1 px-3 text-xs disabled:animate-pulse">
                                {isRecalculating ? 'Calculando...' : 'Calcular Ahora'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {renderContent()}
        </div>
    );
}

export default KPIsModule;